import {
  Button,
  Checkbox,
  DeleteIcon,
  GridColumn,
  GridIcon,
  GridRow,
  Input,
  Select,
  SelectOptionType,
} from '@bp/ui-components';
import { useSortable } from '@dnd-kit/sortable';
import classNames from 'classnames';
import { useFormikContext } from 'formik';
import { observer } from 'mobx-react-lite';
import { CSSProperties, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { SingleValue } from 'react-select';
import { LessonFormTeacherType, LessonFormType } from '../../types';
import styles from './LessonTeacherFormItem.module.scss';
import { LessonTeacherTeachingLoadField } from './LessonTeacherTeachingLoadField';

/**
 * Sortier-Id einer Lehrkraft-Zeile. Muss in Formular und Item identisch berechnet werden,
 * damit SortableContext und useSortable dieselben Ids sehen.
 */
export const lessonTeacherSortableId = (teacher: LessonFormTeacherType, index: number): string =>
  teacher.person?.uuid ?? `teacher-${index}`;

type LessonTeacherFormItemProps = {
  index: number;
  qualifiedTeachers: {
    uuid: string;
    listName: string;
  }[];
  otherTeachers: {
    uuid: string;
    listName: string;
  }[];
  teacher: LessonFormTeacherType;
  disabled: boolean;
  onDelete: () => void;
};

export const LessonTeacherFormItem = observer(
  ({ index, qualifiedTeachers, otherTeachers, teacher, disabled, onDelete }: LessonTeacherFormItemProps) => {
    const { t } = useTranslation();

    const { handleChange, setFieldTouched, setFieldValue } = useFormikContext<LessonFormType>();

    const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
      id: lessonTeacherSortableId(teacher, index),
      disabled,
    });

    const dragStyle: CSSProperties | undefined = transform
      ? {
          transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
          transition,
        }
      : undefined;

    return (
      <div
        ref={setNodeRef}
        style={dragStyle}
        className={classNames(styles['lesson-teacher-form-item'], {
          [styles.dragging]: isDragging,
        })}
      >
        <span
          ref={setActivatorNodeRef}
          className={classNames(styles.handler, { [styles.disabled]: disabled })}
          data-title={t('common.order')}
          {...listeners}
          {...attributes}
        >
          <span className={styles.position}>{index + 1}</span>
          <GridIcon />
        </span>

        <div className={styles.content}>
          <GridRow spacingBottom='m' spacingTop='none'>
            <GridColumn width={10}>
              <GridRow spacingBottom='none' spacingTop='none'>
                <GridColumn width={10}>
                  <GridRow spacingBottom='none' spacingTop='none'>
                    <GridColumn width={6}>
                      <Select
                        isSearchable
                        readonly={disabled}
                        name={`teachers.${index}.person`}
                        label={t('persons.title', { count: 1 })}
                        hasGroups
                        options={[
                          ...qualifiedTeachers.map((person) => ({
                            value: person.uuid,
                            label: person.listName ?? '',
                            group: t('qualifications.withQualification'),
                          })),
                          ...otherTeachers.map((person) => ({
                            value: person.uuid,
                            label: person.listName ?? '',
                            group: t('qualifications.withoutQualification'),
                          })),
                        ]}
                        defaultValue={{
                          value: teacher.person?.uuid,
                          label: teacher.person?.displayNameShort ?? '',
                        }}
                        onChange={(option) => {
                          const opt = option as SingleValue<SelectOptionType>;
                          setFieldTouched(`teachers.${index}.person`, true);
                          setFieldValue(`teachers.${index}.person`, {
                            uuid: opt?.value,
                            displayNameShort: opt?.label,
                          });
                        }}
                      />
                    </GridColumn>
                    <GridColumn width={6}>
                      <Suspense
                        fallback={<Input name={'preview'} label={t('lesson.person.teachingLoadHours')} readonly />}
                      >
                        <LessonTeacherTeachingLoadField teacher={teacher} index={index} />
                      </Suspense>
                    </GridColumn>
                  </GridRow>
                  <GridRow spacingBottom='none' spacingTop='none'>
                    <Input
                      readonly={disabled}
                      name={`teachers.${index}.description`}
                      onChange={handleChange}
                      value={teacher.description ?? ''}
                      label={t('lesson.person.description')}
                    />
                  </GridRow>
                </GridColumn>
                <GridColumn width={2} className='mt-3'>
                  <Checkbox
                    disabled={disabled}
                    name={`teachers.${index}.writesCertificate`}
                    checked={teacher.writesCertificate ?? false}
                    onChange={(event) => {
                      setFieldValue(`teachers.${index}.writesCertificate`, event.target.checked, true);
                      setFieldTouched(`teachers.${index}.writesCertificate`, true);
                    }}
                    label={t('lesson.person.writesCertificate')}
                  />
                  <Checkbox
                    name={`teachers.${index}.teachingLoad`}
                    checked={teacher.teachingLoad ?? false}
                    onChange={(event) => {
                      setFieldValue(`teachers.${index}.teachingLoad`, event.target.checked, true);
                      setFieldTouched(`teachers.${index}.teachingLoad`, true);
                    }}
                    label={t('lesson.person.teachingLoad')}
                  />
                  <Checkbox
                    disabled={disabled}
                    name={`teachers.${index}.present`}
                    checked={teacher.present ?? false}
                    onChange={(event) => {
                      setFieldValue(`teachers.${index}.present`, event.target.checked, true);
                      setFieldTouched(`teachers.${index}.present`, true);
                    }}
                    label={t(`lesson.person.present`)}
                  />
                </GridColumn>
              </GridRow>
            </GridColumn>
            <GridColumn width={2} className='mt-5' align='end'>
              <Button hierarchy='tertiary' disabled={disabled} onClick={onDelete} icon={<DeleteIcon />} />
            </GridColumn>
          </GridRow>
        </div>
      </div>
    );
  },
);
